import { getField, updateField } from "vuex-map-fields";
import { vm } from "@/main";
import axios from "../plugins/axios";
import { objectToQueryString, buildIndustryMap } from "@/helpers/index";

const MAX_CACHE_SIZE = 20; // Set the max number of cached queries

// Refusals the Edit/Add Brokerage form shows under their own input: not toasted as well.
const BROKERAGE_FORM_REFUSALS = ["PROPERTY_IN_USE", "BROKERAGE_CONTACT_TAKEN", "FIELD_MISSING"];

const brokerageData = () => {
  return {
    name: "",
    primaryContact: "",
    mainOfficeAddress: {
      address: null,
      city: null,
      state: null,
      zipCode: null,
    },
    officePhone: null,
    logo: null,
    properties: [""],
  };
};

export default {
  namespaced: true,
  state: () => ({
    // COMPANIES PAGE
    organizationListLoading: false,
    organizationQueryCacheMap: new Map(),
    dropdownOrganizationQueryCacheMap: new Map(),
    organizationBrokeragesQueryCacheMap: new Map(),
    brokerageQueryCacheMap: new Map(),
    brokersQueryCacheMap: new Map(),
    brokerBrokeragesQueryCacheMap: new Map(),
    organizationList: [],
    organizationPageLoading: false,
    getOrganizationListApiStatus: null,
    organizationApiStatus: {
      currentPage: 1,
      totalPages: 1,
    },
    dropdownOrganizationApiStatus: {
      currentPage: 1,
      totalPages: 1,
      loading: false,
    },
    selectedCompanyFilters: [],
    selectedJoinedFilter: null,
    selectedLastActivityFilter: null,
    selectedStatusFilters: [],
    selectedBrokerageFilters: [],
    selectedView: "list",
    brokerages: [], // Organization Brokerages
    brokerBrokerages: [],
    customerPipelinePanel: false,
    customerSource: null,
    selectedOrganization: null,
    organizationControlInformation: null,
    associatedUsers: [],

    // BROKERS PAGE
    brokerList: [],
    brokerListLoading: false,
    brokerBrokeragesLoading: false,
    brokerSidePanel: false,
    selectedBroker: null,
    selectedBrokerClients: [],
    selectedBrokerLocalOffice: null,
    addBrokerDialog: false,
    currentStep: 1,
    totalSteps: 4,
    brokerageLocalOffices: [],
    addContributorDialog: false,
    locatedClient: null,
    colleagueList: [],
    contributorsList: [],
    addBrokerLocalOfficeDialog: false,
    brokerApiStatus: {
      currentPage: 1,
      totalPages: 0,
      totalCount: 0,
    },

    // BROKERAGES PAGE
    brokerageData: brokerageData(),
    editBrokerage: false,
    brokeragesList: [],
    brokeragesListLoading: false,
    dropdownBrokeragesList: [],
    dropdownBrokeragesListLoading: false,
    brokerageSidePanel: false,
    selectedBrokerage: null,
    addBrokerageDialog: false,
    addEditLocalOfficeDialog: false,
    localOfficeData: null,
    selectedBrokerageScreenFilter: null,

    selectedCarrierTenantIds: [],
    brokerageApiStatus: {
      currentPage: 1,
      totalPages: 0,
      totalCount: 0,
    },

    // ANALYTICS PAGE
    ownersList: [],
    ownerListLoading: false,
    selectedOwnerFilters: [],
    analyticsList: [],
    analyticsListLoading: false,
    selectedAnalytics: null,
    selectedCohortDetails: null,

    // COMPANY USERS PAGE
    companyUsersApiStatus: {
      currentPage: 1,
      totalPages: 1,
    },
    dropdownOrganizationList: null,

    // SALES PERSON PAGE
    salesPersonsApiStatus: {
      currentPage: 1,
      totalPages: 1,
    },

    // PARTNERS PAGE
    partnersList: [],
    partnersLoading: false,
    partnerSidePanel: false,
    selectedPartner: null,
    addPartnerDialog: false,

    // PARTNER ADS PAGE
    partnerAdsList: [],
    partnerAdsLoading: false,
    partnerAdsTotalCount: 0,

    // Remembered last payload + debounce handle so Pusher events can refetch
    // the Customer Pipeline list using the user's active filters/page/sort,
    // guaranteeing the row shape matches what the side panel expects.
    lastOrganizationsPayload: null,
    organizationsRefetchDebounce: null,

    // Same pattern for brokerages list — Pusher brokerage* events trigger a
    // debounced refetch using the user's active filters so the row shape
    // stays authoritative.
    lastBrokeragesPayload: null,
    brokeragesRefetchDebounce: null,

    // Brokers list refetch debounce.
    lastBrokersPayload: null,
    brokersRefetchDebounce: null,

    // Contributors (delegation) refetch debounce. Keyed by the broker whose
    // contributor list is currently loaded in the AddContributor modal /
    // ContributorsSection panel.
    contributorsRefetchDebounce: null,

    // Brokerage detail (side panel) refetch — used by local-office-team and
    // carrier-settings events that affect a single brokerage's row.
    brokerageDetailRefetchDebounce: null,
  }),

  mutations: {
    updateField,

    setState(state, payload) {
      state[payload.key] = payload.value;
    },

    resetAddBrokerageData(state) {
      state.editBrokerage = false;
      state.brokerageData = brokerageData();
    },

    deleteBroker(state, entityId) {
      // Splice every match by entity_id (defensive against duplicates), then
      // scrub the broker row from every cached query result so a later
      // filter/page change can't restore it from cache.
      for (let i = state.brokerList.length - 1; i >= 0; i--) {
        if (state.brokerList[i].entity_id == entityId) {
          state.brokerList.splice(i, 1);
        }
      }
      if (state.brokersQueryCacheMap) {
        for (const [key, value] of state.brokersQueryCacheMap.entries()) {
          if (!value || !Array.isArray(value.brokers)) continue;
          const before = value.brokers.length;
          value.brokers = value.brokers.filter((b) => b.entity_id != entityId);
          if (value.brokers.length !== before) {
            value.total_count = Math.max(0, (value.total_count || before) - (before - value.brokers.length));
            state.brokersQueryCacheMap.set(key, value);
          }
        }
      }
    },

    updateBrokerStatus(state, { entityId, status }) {
      const broker = state.brokerList.find((b) => b.entity_id === entityId);
      if (broker) broker.status = status;
    },

    // The panel's own role: from the detail response, or from a `brokerUpdated` row.
    // `client_role_known` marks the role as confirmed by the server for this broker;
    // a fresh copy of a list row does not carry it.
    setSelectedBrokerRole(state, { entityId, clientRole }) {
      if (!entityId || !clientRole || state.selectedBroker?.entity_id !== entityId) return;
      state.selectedBroker = { ...state.selectedBroker, client_role: clientRole, client_role_known: true };
    },

    // After a saved role change: the open panel, the listed row, and every cached
    // page (the row may sit on a page that is not on screen).
    updateBrokerRole(state, { entityId, clientRole }) {
      if (state.selectedBroker?.entity_id === entityId) {
        state.selectedBroker = { ...state.selectedBroker, client_role: clientRole, client_role_known: true };
      }
      const index = state.brokerList.findIndex((b) => b.entity_id === entityId);
      if (index !== -1) {
        state.brokerList.splice(index, 1, { ...state.brokerList[index], client_role: clientRole });
      }
      state.brokersQueryCacheMap.clear();
    },

    updateSelectedBroker(state, data) {
      // for update data if side panel open for display selected broker details
      if (state.selectedBroker?.entity_id == data.broker?.entity_id) {
        // This event's broker carries no role; keep the one the panel already shows,
        // and whether it was confirmed.
        const clientRole = data.broker?.client_role || state.selectedBroker?.client_role;
        const known = !!(data.broker?.client_role || state.selectedBroker?.client_role_known);
        state.selectedBroker = clientRole
          ? { ...data.broker, client_role: clientRole, ...(known ? { client_role_known: true } : {}) }
          : data.broker;
        state.selectedBrokerClients = data.members;
        state.selectedBrokerLocalOffice = data.local_office;
      }
    },

    setBrokerageLocalOffices(state, data) {
      state.brokerageLocalOffices = data;
    },

    updateBrokeragesList(state, { query, data }) {
      if (state.brokerageQueryCacheMap.size >= MAX_CACHE_SIZE) {
        // Remove the oldest entry (first inserted key)
        const oldestKey = state.brokerageQueryCacheMap.keys().next().value;
        state.brokerageQueryCacheMap.delete(oldestKey);
      }

      state.brokerageQueryCacheMap.set(query, {
        brokerages: data.brokerages,
        total_count: data.total_count,
      });

      state.brokeragesList = [...data.brokerages];
    },

    deleteBrokerage(state, entityId) {
      // Splice every match by entity_id and scrub the row from every cached
      // brokerages query result so a later filter/page change can't restore
      // it from cache.
      for (let i = state.brokeragesList.length - 1; i >= 0; i--) {
        if (state.brokeragesList[i].entity_id == entityId) {
          state.brokeragesList.splice(i, 1);
        }
      }
      if (state.brokerageQueryCacheMap) {
        for (const [key, value] of state.brokerageQueryCacheMap.entries()) {
          if (!value || !Array.isArray(value.brokerages)) continue;
          const before = value.brokerages.length;
          value.brokerages = value.brokerages.filter((b) => b.entity_id != entityId);
          if (value.brokerages.length !== before) {
            value.total_count = Math.max(0, (value.total_count || before) - (before - value.brokerages.length));
            state.brokerageQueryCacheMap.set(key, value);
          }
        }
      }
    },

    updateBrokerageList(state, data) {
      let index = state.brokeragesList.findIndex((e) => e.entity_id == data.entity_id);
      const selectedBrokerageLocalOffices =
        state.selectedBrokerage?.entity_id == data.entity_id && Array.isArray(state.selectedBrokerage?.local_offices)
          ? state.selectedBrokerage.local_offices
          : [];
      const normalizedBrokerage = {
        ...data,
        local_offices: Array.isArray(data?.local_offices) ? data.local_offices : selectedBrokerageLocalOffices,
      };
      if (index < 0) {
        state.brokeragesList.push(normalizedBrokerage);
      } else {
        vm.$set(state.brokeragesList, index, normalizedBrokerage);
        if (state.selectedBrokerage && String(state.selectedBrokerage.entity_id) === String(data.entity_id)) {
          state.selectedBrokerage = normalizedBrokerage;
        }
      }
    },

    setColleagueList(state, payload) {
      state.colleagueList = payload;
    },

    setContributorsList(state, payload) {
      state.contributorsList = payload;
    },

    addBrokerageLocalOffice(state, data) {
      if (state.selectedBrokerage) {
        state.selectedBrokerage?.local_offices?.push(data);
      }
    },

    updateBrokerageLocalOffice(state, data) {
      if (state.selectedBrokerage) {
        let index = state.selectedBrokerage?.local_offices?.findIndex((e) => e.entity_id == data.entity_id);
        if (index > -1) {
          vm.$set(state.selectedBrokerage.local_offices, index, data);
        }
      }
    },

    deleteBrokerageLocalOffice(state, localOfficeId) {
      if (state.selectedBrokerage && Array.isArray(state.selectedBrokerage.local_offices)) {
        const index = state.selectedBrokerage.local_offices.findIndex((e) => e.entity_id == localOfficeId);
        if (index > -1) {
          state.selectedBrokerage.local_offices.splice(index, 1);
        }
      }
    },

    updateOrganizationList(state, { data, query }) {
      const brokerageDict = {};

      const organizationList = [];

      for (const el of data.results) {
        const { brokerages, ...rest } = el;

        const brokerageIds = brokerages.map((brokerage) => {
          const { organization_id, organization_name } = brokerage;

          const lowerOrgId = organization_id?.toLowerCase();

          if (!brokerageDict[lowerOrgId]) {
            brokerageDict[lowerOrgId] = { label: organization_name, id: organization_id };
          }

          return organization_id;
        });

        organizationList.push({ ...rest, brokerages, brokerageIds });
      }

      state.organizationList = [...organizationList];

      if (state.organizationQueryCacheMap.size >= MAX_CACHE_SIZE) {
        // Remove the oldest entry (first inserted key)
        const oldestKey = state.organizationQueryCacheMap.keys().next().value;
        state.organizationQueryCacheMap.delete(oldestKey);
      }

      // Add the new entry (latest query result)
      state.organizationQueryCacheMap.set(query, { results: [...organizationList], total_count: data.total_count });
      state.organizationApiStatus.totalPages = Math.ceil(data.total_count / 30);
    },

    updateOrganizationBrokerages(state, { data, query }) {
      state.brokerages = data;

      if (state.organizationBrokeragesQueryCacheMap.size >= MAX_CACHE_SIZE) {
        // Remove the oldest entry (first inserted key)
        const oldestKey = state.organizationBrokeragesQueryCacheMap.keys().next().value;
        state.organizationBrokeragesQueryCacheMap.delete(oldestKey);
      }

      state.organizationBrokeragesQueryCacheMap.set(query, { results: data });
    },

    updateBrokerBrokerages(state, { data, query }) {
      state.brokerBrokerages = data;

      if (state.brokerBrokeragesQueryCacheMap.size >= MAX_CACHE_SIZE) {
        // Remove the oldest entry (first inserted key)
        const oldestKey = state.brokerBrokeragesQueryCacheMap.keys().next().value;
        state.brokerBrokeragesQueryCacheMap.delete(oldestKey);
      }

      state.brokerBrokeragesQueryCacheMap.set(query, { results: data });
    },

    updateDropdownOrganizationList(state, { data, query }) {
      if (state.dropdownOrganizationQueryCacheMap.size >= MAX_CACHE_SIZE) {
        // Remove the oldest entry (first inserted key)
        const oldestKey = state.dropdownOrganizationQueryCacheMap.keys().next().value;
        state.dropdownOrganizationQueryCacheMap.delete(oldestKey);
      }

      state.dropdownOrganizationQueryCacheMap.set(query, {
        results: data.results,
        total_count: data.total_count,
      });
    },

    updateBrokersList(state, { data, query }) {
      state.brokerList = [...data.brokers];
      if (state.brokersQueryCacheMap.size >= MAX_CACHE_SIZE) {
        // Remove the oldest entry (first inserted key)
        const oldestKey = state.brokersQueryCacheMap.keys().next().value;
        state.brokersQueryCacheMap.delete(oldestKey);
      }
      state.brokersQueryCacheMap.set(query, {
        brokers: data.brokers,
        total_count: data.total_count,
      });
    },

    updateBrokerageDetails(state, data) {
      state.selectedBroker.organization_id = data.brokerage?.entity_id;
      state.selectedBroker.organization_name = data.brokerage?.name;
      let index = state.brokerList.findIndex((e) => e.entity_id == data.broker?.entity_id);
      if (index > -1) {
        let broker = state.brokerList[index];
        broker.organization_id = data.brokerage?.entity_id;
        broker.organization_name = data.brokerage?.name;
        state.selectedBrokerLocalOffice = data.local_office;
        vm.$set(state.brokerList, index, broker);
      }
    },

    upsertOrganization(state, company) {
      if (!company?.entity_id) return;
      // Normalize incoming row to the same shape stored by updateOrganizationList
      // (which derives brokerageIds from the brokerages array).
      const brokerages = Array.isArray(company.brokerages) ? company.brokerages : [];
      const brokerageIds = brokerages.map((b) => b.organization_id);
      const normalized = { ...company, brokerages, brokerageIds };

      const index = state.organizationList.findIndex((e) => e.entity_id == normalized.entity_id);
      if (index > -1) {
        vm.$set(state.organizationList, index, normalized);
      } else {
        state.organizationList.splice(0, 0, normalized);
      }

      // Keep the side-panel-selected organization in sync if it's the same row.
      if (state.selectedOrganization && state.selectedOrganization.entity_id == normalized.entity_id) {
        state.selectedOrganization = { ...state.selectedOrganization, ...normalized };
      }
    },

    removeOrganization(state, id) {
      // Match by either entity_id or organization_id — admin list rows key by
      // organization.entity_id (so they coincide), but Pusher payloads from the
      // delete fan-out only guarantee `organization_id`. Splice all matches
      // (defensive against duplicates from past stale upserts).
      for (let i = state.organizationList.length - 1; i >= 0; i--) {
        const row = state.organizationList[i];
        if (row.entity_id == id || row.organization_id == id) {
          state.organizationList.splice(i, 1);
        }
      }
      // Drop the row from EVERY cached query result too, otherwise a refetch
      // with a cache hit (or a later filter change that hits cache) restores
      // the deleted row.
      if (state.organizationQueryCacheMap) {
        for (const [key, value] of state.organizationQueryCacheMap.entries()) {
          if (!value || !Array.isArray(value.results)) continue;
          const before = value.results.length;
          value.results = value.results.filter((e) => e.entity_id != id && e.organization_id != id);
          if (value.results.length !== before) {
            value.total_count = Math.max(0, (value.total_count || before) - (before - value.results.length));
            state.organizationQueryCacheMap.set(key, value);
          }
        }
      }
    },
  },

  getters: {
    getField,
    organizationList: (state) => state.organizationList,
    partnersList: (state) => state.partnersList,
    partnerAdsList: (state) => state.partnerAdsList,
    partnerAdsLoading: (state) => state.partnerAdsLoading,
    partnerAdsTotalCount: (state) => state.partnerAdsTotalCount,
  },

  actions: {
    async getOrganizations({ commit, state }, payload) {
      try {
        commit("setState", { key: "lastOrganizationsPayload", value: payload ?? null });
        let url = "/organization_information";
        let query = objectToQueryString(payload ?? {});
        url += "?" + query;

        let cacheHit = state.organizationQueryCacheMap.get(query);
        if (cacheHit) {
          commit("setState", { key: "organizationList", value: cacheHit.results });
          commit("setState", {
            key: "organizationApiStatus",
            value: { ...state.organizationApiStatus, totalPages: Math.ceil(cacheHit.total_count / 30) },
          });
          return;
        }

        commit("setState", { key: "organizationPageLoading", value: true });
        const { data } = await axios.get(url);
        if (data.success) {
          commit("updateOrganizationList", { data, query });
        }
        return data;
      } catch (e) {
        console.error(e);
      } finally {
        commit("setState", { key: "organizationPageLoading", value: false });
        commit("setState", { key: "organizationListLoading", value: false });
      }
    },

    // Pusher: a company row was created/updated on the backend.
    //
    // The Pusher payload uses the broker-member serializer (entity_id =
    // client_broker link row), but the admin Customer Pipeline list keys
    // rows by organization.entity_id and reads admin-only fields the
    // broker-member shape doesn't carry (brokerages, IT budget, etc.).
    // Trying to upsert the Pusher row in place yielded duplicated/partial
    // rows and crashed the side panel on click. Instead, debounce a
    // refetch of the user's current filtered/paginated view via the same
    // /organization_information endpoint so the list always matches what
    // the panel expects. Burst creates (broker bulk import) collapse to
    // a single refetch.
    _scheduleOrganizationsRefetch({ state, dispatch, commit }) {
      if (state.organizationsRefetchDebounce) {
        clearTimeout(state.organizationsRefetchDebounce);
      }
      const handle = setTimeout(() => {
        commit("setState", { key: "organizationsRefetchDebounce", value: null });
        const payload = state.lastOrganizationsPayload;
        if (!payload) return;
        // Wipe the whole cache — any cached query (different page/filter)
        // could be holding a now-stale row; clearing only the current query
        // would let staleness bleed through on the user's next filter change.
        if (state.organizationQueryCacheMap?.clear) {
          state.organizationQueryCacheMap.clear();
        }
        dispatch("getOrganizations", payload);
      }, 400);
      commit("setState", { key: "organizationsRefetchDebounce", value: handle });
    },

    pusherCompanyCreated({ state, dispatch }, company) {
      if (!company) return;

      const hasActiveDateOrSearchFilter = !!state.selectedJoinedFilter || !!state.selectedLastActivityFilter;
      if (hasActiveDateOrSearchFilter) return;

      if (state.selectedStatusFilters?.length) {
        if (!state.selectedStatusFilters.includes(company.company_status)) return;
      }

      dispatch("_scheduleOrganizationsRefetch");
    },

    pusherCompanyUpdated({ state, dispatch }, company) {
      if (!company) return;
      // Only refetch if the row is currently in the user's visible list;
      // otherwise the update is irrelevant to this view.
      const orgId = company.organization_id || company.entity_id;
      const existsInList = state.organizationList.some((e) => e.entity_id == orgId || e.organization_id == orgId);
      if (!existsInList) return;

      dispatch("_scheduleOrganizationsRefetch");
    },

    // Pusher: a company row was deleted on the backend.
    //
    // Payload is small (at least `organization_id`). If the row is visible
    // in the current list, splice it out for instant feedback; otherwise
    // skip to avoid flicker. Then debounce a refetch so totals/pagination
    // converge with the server view.
    pusherCompanyDeleted({ dispatch, commit }, payload) {
      if (!payload) return;
      const orgId = payload.organization_id || payload.entity_id;
      if (!orgId) return;

      // Unconditional: always splice + cache-scrub. The previous
      // `existsInList` guard let stale rows survive on tabs whose visible page
      // didn't contain the row but whose cache did, producing the "duplicate
      // tab still shows the deleted customer" bug.
      commit("removeOrganization", orgId);
      dispatch("_scheduleOrganizationsRefetch");
    },

    // ------------------------------------------------------------------
    // Brokerages list — Pusher refetch
    // ------------------------------------------------------------------
    //
    // Mirrors the organizations pattern: every Pusher brokerage* event
    // debounces a refetch of the user's currently filtered/paginated
    // brokerages list so the row shape always matches what the side panel
    // expects. Bursts (bulk imports / cascading updates) collapse to one
    // refetch.
    _scheduleBrokeragesRefetch({ state, dispatch, commit }) {
      if (state.brokeragesRefetchDebounce) {
        clearTimeout(state.brokeragesRefetchDebounce);
      }
      const handle = setTimeout(() => {
        commit("setState", { key: "brokeragesRefetchDebounce", value: null });
        const payload = state.lastBrokeragesPayload;
        if (!payload) return;
        // Wipe the WHOLE cache, not just the current query — any other page /
        // filter cached on this tab could otherwise restore the now-stale row
        // on the user's next filter or page change.
        if (state.brokerageQueryCacheMap?.clear) {
          state.brokerageQueryCacheMap.clear();
        }
        dispatch("getBrokerageData", payload);
      }, 400);
      commit("setState", { key: "brokeragesRefetchDebounce", value: handle });
    },

    // Unconditional create/update/delete: always refetch the current view and
    // wipe the brokerages query cache. The previous existsInList guards meant
    // cached rows survived on tabs whose visible page didn't contain the row,
    // producing "duplicate tab still shows the brokerage" bugs.
    pusherBrokerageCreated({ dispatch }, brokerage) {
      if (!brokerage) return;
      dispatch("_scheduleBrokeragesRefetch");
    },

    pusherBrokerageUpdated({ state, dispatch }, brokerage) {
      if (!brokerage) return;
      const entityId = brokerage.entity_id;
      const isSelected =
        entityId && state.selectedBrokerage && String(state.selectedBrokerage.entity_id) === String(entityId);
      dispatch("_scheduleBrokeragesRefetch");
      if (isSelected) dispatch("_scheduleBrokerageDetailRefetch");
    },

    pusherBrokerageDeleted({ dispatch, commit }, payload) {
      if (!payload) return;
      const entityId = payload.entity_id || payload.organization_id;
      if (!entityId) return;
      commit("deleteBrokerage", entityId);
      dispatch("_scheduleBrokeragesRefetch");
    },

    // ------------------------------------------------------------------
    // Brokers list — Pusher refetch
    // ------------------------------------------------------------------
    _scheduleBrokersRefetch({ state, dispatch, commit }) {
      if (state.brokersRefetchDebounce) {
        clearTimeout(state.brokersRefetchDebounce);
      }
      const handle = setTimeout(() => {
        commit("setState", { key: "brokersRefetchDebounce", value: null });
        const payload = state.lastBrokersPayload;
        if (!payload) return;
        if (state.brokersQueryCacheMap?.clear) {
          state.brokersQueryCacheMap.clear();
        }
        dispatch("getBrokersList", payload);
      }, 400);
      commit("setState", { key: "brokersRefetchDebounce", value: handle });
    },

    // Unconditional refetch + full cache wipe on broker create/update/delete.
    // Broker-move on the backend emits to the SUPER_ADMIN channel with the
    // broker's NEW brokerage_id; with the prior existsInList guard, a tab
    // filtering by the OLD brokerage would silently miss the event and keep
    // showing the moved broker until refresh.
    pusherBrokerCreated({ dispatch }, broker) {
      if (!broker) return;
      dispatch("_scheduleBrokersRefetch");
    },

    pusherBrokerUpdated({ dispatch, commit }, broker) {
      if (!broker) return;
      // Another super admin changed the role of the broker in the open panel.
      commit("setSelectedBrokerRole", { entityId: broker.entity_id, clientRole: broker.client_role });
      dispatch("_scheduleBrokersRefetch");
    },

    pusherBrokerDeleted({ dispatch, commit }, payload) {
      if (!payload) return;
      const entityId = payload.entity_id;
      if (!entityId) return;
      commit("deleteBroker", entityId);
      dispatch("_scheduleBrokersRefetch");
    },

    // ------------------------------------------------------------------
    // Delegation (contributor) — refetch the currently open contributor list
    // ------------------------------------------------------------------
    _scheduleContributorsRefetch({ state, dispatch, commit }) {
      if (state.contributorsRefetchDebounce) {
        clearTimeout(state.contributorsRefetchDebounce);
      }
      const handle = setTimeout(() => {
        commit("setState", { key: "contributorsRefetchDebounce", value: null });
        const brokerId = state.selectedBroker?.entity_id;
        if (!brokerId) return;
        dispatch("getBrokerContributors", brokerId);
      }, 400);
      commit("setState", { key: "contributorsRefetchDebounce", value: handle });
    },

    pusherDelegationCreated({ state, dispatch }, delegation) {
      if (!delegation) return;
      // Refetch contributors list if it corresponds to the broker we're viewing.
      const selectedBrokerId = state.selectedBroker?.entity_id;
      if (!selectedBrokerId) return;
      const brokerId = delegation.broker_id || delegation.delegator_broker_id;
      if (brokerId && String(brokerId) !== String(selectedBrokerId)) return;
      dispatch("_scheduleContributorsRefetch");
    },

    pusherDelegationDeleted({ state, dispatch }, payload) {
      if (!payload || !state.selectedBroker?.entity_id) return;
      dispatch("_scheduleContributorsRefetch");
    },

    // ------------------------------------------------------------------
    // Local office team members — affects a brokerage's local_offices row
    // ------------------------------------------------------------------
    //
    // Local-office team membership is rendered inside the brokerage side
    // panel (via `selectedBrokerage.local_offices`) and contributes to
    // counts shown in the brokerages list row. Refetch the open brokerage
    // detail plus the brokerages list so both views converge.
    _scheduleBrokerageDetailRefetch({ state, dispatch, commit }) {
      if (state.brokerageDetailRefetchDebounce) {
        clearTimeout(state.brokerageDetailRefetchDebounce);
      }
      const handle = setTimeout(() => {
        commit("setState", { key: "brokerageDetailRefetchDebounce", value: null });
        const orgId = state.selectedBrokerage?.entity_id;
        if (!orgId) return;
        dispatch("getBrokerageDetailsByOrgId", orgId).then((res) => {
          if (res?.brokerage) {
            commit("updateBrokerageList", res.brokerage);
          }
        });
      }, 400);
      commit("setState", { key: "brokerageDetailRefetchDebounce", value: handle });
    },

    pusherLocalOfficeTeamMemberCreated({ dispatch }, payload) {
      if (!payload) return;
      dispatch("_scheduleBrokeragesRefetch");
      dispatch("_scheduleBrokerageDetailRefetch");
    },

    pusherLocalOfficeTeamMemberUpdated({ dispatch }, payload) {
      if (!payload) return;
      dispatch("_scheduleBrokeragesRefetch");
      dispatch("_scheduleBrokerageDetailRefetch");
    },

    pusherLocalOfficeTeamMemberDeleted({ dispatch }, payload) {
      if (!payload) return;
      dispatch("_scheduleBrokeragesRefetch");
      dispatch("_scheduleBrokerageDetailRefetch");
    },

    // ------------------------------------------------------------------
    // Carrier settings — refetch for the affected brokerage when the side
    // panel / settings modal is open on that brokerage.
    // ------------------------------------------------------------------
    pusherCarrierSettingsUpdated({ state, dispatch }, payload) {
      if (!payload) return;
      const targetOrgId = payload.organization_id || payload.brokerage_id || payload.entity_id || null;
      const selectedOrgId = state.selectedBrokerage?.entity_id;
      if (!selectedOrgId) return;
      if (targetOrgId && String(targetOrgId) !== String(selectedOrgId)) return;
      dispatch("getBrokerageCarrierSettings", selectedOrgId);
    },

    async getDropdownOrganizations({ commit, state }, payload) {
      try {
        let url = "/organization_information/dropdown";
        let query = objectToQueryString(payload ?? {});
        url += "?" + query;

        let cacheHit = state.dropdownOrganizationQueryCacheMap.get(query);
        if (cacheHit) {
          commit("setState", {
            key: "dropdownOrganizationApiStatus",
            value: {
              ...state.dropdownOrganizationApiStatus,
              totalPages: Math.ceil(cacheHit.total_count / 30),
              loading: false,
            },
          });
          return cacheHit.results;
        }

        commit("setState", {
          key: "dropdownOrganizationApiStatus",
          value: {
            ...state.dropdownOrganizationApiStatus,
            loading: true,
          },
        });
        const { data } = await axios.get(url);
        if (data.success) {
          commit("setState", {
            key: "dropdownOrganizationApiStatus",
            value: {
              ...state.dropdownOrganizationApiStatus,
              loading: false,
            },
          });
          commit("updateDropdownOrganizationList", { data, query });
          return data.results;
        }
      } catch (e) {
        console.error(e);
      } finally {
        commit("setState", {
          key: "dropdownOrganizationApiStatus",
          value: {
            ...state.dropdownOrganizationApiStatus,
            loading: false,
          },
        });
      }
    },

    async getOrganizationBrokerages({ commit, state }, payload) {
      try {
        let url = "/organization_information/brokerages";
        let query = objectToQueryString(payload ?? {});
        url += "?" + query;

        let cacheHit = state.organizationBrokeragesQueryCacheMap.get(query);
        if (cacheHit) {
          commit("setState", { key: "brokerages", value: cacheHit.results });
          return;
        }

        commit("setState", { key: "organizationPageLoading", value: true });
        const { data } = await axios.get(url);
        if (data.success) {
          commit("updateOrganizationBrokerages", { data: data.results, query });
        }
        return data;
      } catch (e) {
        console.error(e);
      } finally {
        commit("setState", { key: "organizationPageLoading", value: false });
        commit("setState", { key: "organizationListLoading", value: false });
      }
    },

    async getBrokerBrokerages({ commit, state }, payload) {
      try {
        let url = "/admin/broker/brokerages";
        let query = objectToQueryString(payload ?? {});
        url += "?" + query;

        let cacheHit = state.brokerBrokeragesQueryCacheMap.get(query);
        if (cacheHit) {
          commit("setState", { key: "brokerBrokerages", value: cacheHit.results });
          return;
        }

        commit("setState", { key: "brokerBrokeragesLoading", value: true });
        const { data } = await axios.get(url);
        if (data.success) {
          commit("updateBrokerBrokerages", { data: data.results, query });
        }
        return data;
      } catch (e) {
        console.error(e);
      } finally {
        commit("setState", { key: "brokerBrokeragesLoading", value: false });
      }
    },

    async deleteOrganization({ commit, dispatch }, payload) {
      const { data } = await axios.delete(`/organization/${payload.entity_id}`);
      if (!data.success) return;
      let message = payload.deleteFromBrokerage ? "Brokerage deleted successfully." : data.message;
      dispatch("showMessage", { message, messageType: "success", messageTitle: "Success" }, { root: true });
      return data;
    },

    async getBrokersList({ commit, state }, payload) {
      commit("setState", { key: "lastBrokersPayload", value: payload ?? null });
      let url = "/admin/broker";
      let query = objectToQueryString(payload ?? {});
      url += "?" + query;

      let cacheHit = state.brokersQueryCacheMap.get(query);
      if (cacheHit) {
        commit("setState", { key: "brokerList", value: cacheHit.brokers });
        commit("setState", {
          key: "brokerApiStatus",
          value: {
            ...state.brokerApiStatus,
            totalCount: cacheHit.total_count,
            totalPages: Math.ceil(cacheHit.total_count / payload.page_size),
          },
        });
        return;
      }

      commit("setState", { key: "brokerListLoading", value: true });
      const { data } = await axios.get(url);
      if (!data.success) {
        commit("setState", { key: "brokerListLoading", value: false });
        return;
      }
      commit("updateBrokersList", { query, data });
      commit("setState", {
        key: "brokerApiStatus",
        value: {
          ...state.brokerApiStatus,
          totalCount: data.total_count,
          totalPages: Math.ceil(data.total_count / payload.page_size),
        },
      });
      commit("setState", { key: "brokerListLoading", value: false });
      return data;
    },

    async getBrokerDetailsByEntity({ commit }, entityId) {
      const { data } = await axios.get(`/admin/broker/${entityId}`);
      if (!data.success) {
        return;
      }
      return data;
    },

    async deleteBrokerAccount({ commit, dispatch }, entityId) {
      commit("deleteBroker", entityId);
      const { data } = await axios.delete(`/admin/broker/${entityId}`);
      if (!data.success) {
        return;
      }
      dispatch("showMessage", { ...data, messageType: "success", messageTitle: "Success" }, { root: true });
    },

    async checkBrokerActivity({ commit }, entityId) {
      const { data } = await axios.get(`/admin/broker/${entityId}/activity_check`);
      if (!data.success) return null;
      return data;
    },

    async getBrokerageePeers({ commit }, entityId) {
      const { data } = await axios.get(`/admin/broker/${entityId}/brokerage_peers`);
      if (!data.success) return [];
      return data.brokers;
    },

    async deactivateBroker({ commit, dispatch }, { entityId, newBrokerId }) {
      const payload = newBrokerId ? { new_broker_id: newBrokerId } : {};
      const { data } = await axios.post(`/admin/broker/${entityId}/deactivate`, payload);
      if (!data.success) return;
      commit("updateBrokerStatus", { entityId, status: "deactivated" });
      dispatch("showMessage", { ...data, messageType: "success", messageTitle: "Success" }, { root: true });
      return data;
    },

    // role: "admin" or "contributor" (shown as "Broker"). A refusal answers
    // success: false; the axios plugin shows its message and nothing changes here.
    async setBrokerRole({ commit, dispatch }, { entityId, role }) {
      const { data } = await axios.post(`/admin/broker/${entityId}/role`, { role });
      if (!data.success) return;
      commit("updateBrokerRole", { entityId, clientRole: data.client_role || role });
      dispatch("showMessage", { ...data, messageType: "success", messageTitle: "Success" }, { root: true });
      return data;
    },

    async checkIfUserExists({ dispatch }, payload) {
      const { data } = await axios.post(`/admin/check_user`, payload);
      if (!data.success) return;
      return data;
    },

    async localOfficeBrokerage({ commit }, organization_id) {
      const { data } = await axios.get(`/local_office/organization/${organization_id}`);
      if (!data.success) return;
      commit("setBrokerageLocalOffices", data.local_offices);
    },

    async createBroker({ dispatch }, payload) {
      const { data } = await axios.post(`/admin/broker`, payload);
      if (!data.success) return data;
      dispatch("showMessage", { ...data, messageType: "success", messageTitle: "Success" }, { root: true });
      return data;
    },

    async createBulkBrokers({ dispatch }, brokers) {
      const { data } = await axios.post(`/admin/broker/bulk`, { brokers });
      if (!data.success) {
        dispatch("showMessage", { ...data, messageType: "error", messageTitle: "Error" }, { root: true });
        return {
          success: false,
          addedCount: 0,
          skipped: data.skipped || [],
          message: data.message,
        };
      }
      dispatch("showMessage", { ...data, messageType: "success", messageTitle: "Success" }, { root: true });
      return {
        success: true,
        addedCount: data.created_count,
        skipped: data.skipped || [],
        brokers: data.brokers || [],
      };
    },

    async importShapeBrokers({ dispatch }, { rows, send_invites = false }) {
      const { data } = await axios.post(`/admin/broker/shape-import`, { rows, send_invites });
      if (!data.success) {
        dispatch("showMessage", { ...data, messageType: "error", messageTitle: "Error" }, { root: true });
        return {
          success: false,
          message: data.message,
          skipped: data.skipped || [],
          organizations_created: data.organizations_created || 0,
          organizations_updated: data.organizations_updated || 0,
          offices_created: data.offices_created || 0,
          offices_updated: data.offices_updated || 0,
          brokers_created: data.brokers_created || 0,
          brokers_updated: data.brokers_updated || 0,
        };
      }
      dispatch("showMessage", { ...data, messageType: "success", messageTitle: "Success" }, { root: true });
      return {
        success: true,
        message: data.message,
        skipped: data.skipped || [],
        brokers: data.brokers || [],
        organizations_created: data.organizations_created || 0,
        organizations_updated: data.organizations_updated || 0,
        offices_created: data.offices_created || 0,
        offices_updated: data.offices_updated || 0,
        brokers_created: data.brokers_created || 0,
        brokers_updated: data.brokers_updated || 0,
      };
    },

    async getCustomerSource({ dispatch, commit }, organizationId) {
      const { data } = await axios.get(`/organization/source/${organizationId}`);
      if (!data.success) {
        return dispatch("showMessage", { ...data, messageType: "error", messageTitle: "Error" }, { root: true });
      }
      commit("setState", { key: "customerSource", value: data });
      return data;
    },

    async getOrganizationDetail({ dispatch, commit }, organizationId) {
      const { data } = await axios.get(`/organization/detail/${organizationId}`);
      if (!data.success) {
        return dispatch("showMessage", { ...data, messageType: "error", messageTitle: "Error" }, { root: true });
      }
      commit("setState", { key: "customerSource", value: { channel: data.channel } });

      let users = [...data.brokers, ...data.clients];
      commit("setState", { key: "associatedUsers", value: users });

      commit("applications/SET_APPLICATIONS", data.applications, { root: true });
      commit("advisory/SET_ADVISORY_DATA", data.advisory_report, { root: true });
      commit("threats/SET_THREAT_PROPERTIES", data.threat_reports, { root: true });
      commit("privacy/SET_PRIVACY_PROPERTIES", data.privacy_reports, { root: true });
      commit("insurability/SET_INSURABILITY_REPORTS", data.insurability_reports, { root: true });
      commit("setState", { key: "organizationControlInformation", value: data.organization_control_information });

      return data;
    },

    async getDropdownBrokerages({ dispatch, commit }) {
      commit("setState", { key: "dropdownBrokeragesListLoading", value: true });
      let url = "/admin/brokerages/dropdown";
      const { data } = await axios.get(url);
      commit("setState", { key: "dropdownBrokeragesListLoading", value: false });
      if (!data.success) {
        return dispatch("showMessage", { ...data, messageType: "error", messageTitle: "Error" }, { root: true });
      }

      commit("setState", { key: "dropdownBrokeragesList", value: data.brokerages });
    },

    async fetchOrganizationControlInformation({ dispatch, commit }, organizationId) {
      const { data } = await axios.get(`/organization_control_information/${organizationId}`);
      if (!data.success) {
        return dispatch("showMessage", { ...data, messageType: "error", messageTitle: "Error" }, { root: true });
      }
      commit("setState", { key: "organizationControlInformation", value: data.results });
      return data;
    },

    async importCompanies({ dispatch }, payload) {
      const { data } = await axios.post("/admin/broker/import_companies", payload);
      if (!data.success) {
        return dispatch("showMessage", { ...data, messageType: "error", messageTitle: "Error" }, { root: true });
      }
      return data;
    },

    async getBrokerageData({ commit, state }, payload) {
      try {
        commit("setState", { key: "lastBrokeragesPayload", value: payload ?? null });
        let url = "/admin/brokerage";
        let query = objectToQueryString(payload ?? {});
        url += "?" + query;

        let cacheHit = state.brokerageQueryCacheMap.get(query);
        if (cacheHit) {
          commit("setState", { key: "brokeragesList", value: cacheHit.brokerages });
          commit("setState", {
            key: "brokerageApiStatus",
            value: {
              ...state.brokerageApiStatus,
              totalPages: Math.ceil(cacheHit.total_count / 30),
              totalCount: cacheHit.total_count,
            },
          });
          return;
        }

        commit("setState", { key: "brokeragesListLoading", value: true });
        const { data } = await axios.get(url);
        if (data.success) {
          commit("updateBrokeragesList", { query, data });

          commit("setState", {
            key: "brokerageApiStatus",
            value: {
              ...state.brokerageApiStatus,
              totalPages: Math.ceil(data.total_count / 30),
              totalCount: data.total_count,
            },
          });
        }
        return data;
      } catch (e) {
        console.error(e);
      } finally {
        commit("setState", { key: "brokeragesListLoading", value: false });
      }
    },

    // A refusal comes back as data. The interceptor already toasts one the form does not name;
    // the ones named in BROKERAGE_FORM_REFUSALS are shown under their input by the form.
    async createBrokerage({ dispatch }, payload) {
      const { data } = await axios.post("/admin/brokerage", payload, {
        withoutErrorHandlerFor: BROKERAGE_FORM_REFUSALS,
      });
      if (data.success) {
        dispatch("showMessage", { ...data, messageType: "success", messageTitle: "Success" }, { root: true });
      }
      return data;
    },

    async updateBrokerage({ dispatch }, payload) {
      let payloadData = { ...payload };
      const organizationId = payload.organization_id;
      delete payload.organization_id;
      const { data } = await axios.post(`/admin/brokerage/${organizationId}`, payloadData, {
        withoutErrorHandlerFor: BROKERAGE_FORM_REFUSALS,
      });
      if (data.success) {
        dispatch("showMessage", { ...data, messageType: "success", messageTitle: "Success" }, { root: true });
      }
      return data;
    },

    async getBrokerageDetailsByOrgId({ dispatch, commit }, organizationId) {
      const { data } = await axios.get(`/admin/brokerage/${organizationId}`);
      if (!data.success) {
        return dispatch("showMessage", { ...data, messageType: "error", messageTitle: "Error" }, { root: true });
      }
      return data;
    },

    async getLocalOfficeDetailsById({ dispatch, commit }, entityId) {
      const { data } = await axios.get(`/admin/local_office_setup/${entityId}`);
      if (!data.success) {
        return dispatch("showMessage", { ...data, messageType: "error", messageTitle: "Error" }, { root: true });
      }
      return data.local_office_setup_data;
    },

    async updateLocalOffice({ dispatch, commit }, payload) {
      let payloadData = { ...payload };
      const entityId = payloadData.entity_id;
      delete payloadData.entity_id;
      const { data } = await axios.post(`/admin/local_office_setup/${entityId}`, payloadData);
      if (!data.success) {
        return dispatch("showMessage", { ...data, messageType: "error", messageTitle: "Error" }, { root: true });
      }
      dispatch("showMessage", { ...data, messageType: "success", messageTitle: "Success" }, { root: true });
      commit("updateBrokerageLocalOffice", data.local_office_setup_data);
      return data;
    },

    async addLocalOfficeByBrokerageId({ dispatch, commit }, payload) {
      const { data } = await axios.post(`/admin/brokerage/${payload.brokerageId}/local_office`, payload.data);
      if (!data.success) {
        return dispatch("showMessage", { ...data, messageType: "error", messageTitle: "Error" }, { root: true });
      }
      dispatch("showMessage", { ...data, messageType: "success", messageTitle: "Success" }, { root: true });
      commit("addBrokerageLocalOffice", data.local_office_setup_data);
      return data;
    },

    async deleteLocalOffice({ dispatch, commit }, entityId) {
      const { data } = await axios.delete(`/admin/local_office_setup/${entityId}`);
      if (!data.success) {
        dispatch("showMessage", { ...data, messageType: "error", messageTitle: "Error" }, { root: true });
        return data;
      }
      dispatch("showMessage", { ...data, messageType: "success", messageTitle: "Success" }, { root: true });
      commit("deleteBrokerageLocalOffice", data.local_office_id || entityId);
      return data;
    },

    async getBrokerageCarrierSettings({ commit, dispatch }, organizationId) {
      try {
        const { data } = await axios.get(`/admin/brokerage/${organizationId}/carrier_settings`);
        if (data.success) {
          const carrierIds = (data.carrier_settings?.carriers || []).map((carrier) => carrier.id);
          commit("setState", { key: "selectedCarrierTenantIds", value: carrierIds });
          return data;
        } else {
          dispatch("showMessage", { ...data, messageType: "error", messageTitle: "Error" }, { root: true });
          return null;
        }
      } catch (error) {
        console.error(error);
        return null;
      }
    },

    async updateBrokerageCarrierSettings({ dispatch }, payload) {
      try {
        const { data } = await axios.post(`/admin/brokerage/${payload.organizationId}/carrier_settings`, {
          carrier_ids: payload.carrierIds,
        });
        if (data.success) {
          dispatch(
            "showMessage",
            {
              message: data.message || "Carrier integration settings updated successfully",
              messageType: "success",
              messageTitle: "Success",
            },
            { root: true }
          );
          return data;
        } else {
          dispatch("showMessage", { ...data, messageType: "error", messageTitle: "Error" }, { root: true });
          return { success: false, message: data.message };
        }
      } catch (error) {
        dispatch(
          "showMessage",
          {
            message: error.response?.data?.message || "Failed to update carrier integration settings",
            messageType: "error",
            messageTitle: "Error",
          },
          { root: true }
        );
        return { success: false, message: error.response?.data?.message || error.message };
      }
    },

    async getBrokerContributors({ commit }, brokerId) {
      try {
        const { data } = await axios.get(`/admin/broker/${brokerId}/contributor`);
        commit("setContributorsList", data.broker_contributors);
        return data;
      } catch (error) {
        console.error(error);
      }
    },

    async addBrokerContributor({ dispatch }, payload) {
      try {
        const { data } = await axios.post(`/admin/broker/${payload.brokerId}/contributor`, payload.data);
        if (!data.success) {
          return dispatch("showMessage", { ...data, messageType: "error", messageTitle: "Error" }, { root: true });
        }
        return data;
      } catch (error) {
        console.error(error);
      }
    },

    async deleteBrokerContributor({ dispatch }, payload) {
      try {
        const endpoint = `/admin/broker/${payload.brokerId}/contributor/${payload.contributorBrokerId}`;
        const { data } = await axios.delete(endpoint);

        if (!data.success) {
          return dispatch("showMessage", { ...data, messageType: "error", messageTitle: "Error" }, { root: true });
        }

        const message = {
          messageType: data.success ? "success" : "error",
          messageTitle: data.success ? "Success" : "Error",
          ...data,
        };

        dispatch("showMessage", message, { root: true });
      } catch (error) {
        console.error(error);
      }
    },

    async getColleagueList({ commit }, brokerId) {
      try {
        const { data } = await axios.get(`/admin/broker/${brokerId}/colleague`);
        commit("setColleagueList", data.brokers);
        return data;
      } catch (error) {
        console.error(error);
      }
    },

    async inviteBroker({ dispatch }, payload) {
      try {
        const { data } = await axios.post(`/admin/invite_broker`, payload);
        if (!data.success) return false;
        dispatch("showMessage", { ...data, messageType: "success", messageTitle: "Success" }, { root: true });
        return true;
      } catch (error) {
        console.error(error);
      }
    },

    async changeBrokerLocalOffice({ dispatch }, payload) {
      try {
        const { data } = await axios.post(`/admin/broker/${payload.brokerId}/local_office`, payload.data);
        if (!data.success) {
          return dispatch("showMessage", { ...data, messageType: "error", messageTitle: "Error" }, { root: true });
        }
        return data;
      } catch (error) {
        console.error(error);
      }
    },

    async getOwnersList({ commit }, payload) {
      try {
        const data = {
          success: true,
          owners: [
            {
              id: 12345,
              name: "Chapin Smith",
            },
            {
              id: 12346,
              name: "Jasmine Romo",
            },
          ],
        };
        commit("setState", { key: "ownersList", value: data.owners });
      } catch (error) {
        console.error(error);
      }
    },
    async getAnalyticsList({ commit, dispatch }, payload) {
      try {
        commit("setState", { key: "analyticsListLoading", value: true });
        const data = {
          success: true,
          analytics: [
            {
              entity_id: "102dd7ec6a4574ac09c655da142e40da1234",
              title: "Broker PMF",
              description: "Broker invites two colleagues, two clients, and makes a market submission.",
              status: 28,
              test_length: 60,
              success_criteria: 80,
              owner: {
                entity_id: "12345",
                name: "Chapin Smith",
              },
              created: "2024-02-17T10:08:03",
              cohort_details: {
                year: "2023",
                details: [
                  {
                    month: "Jan",
                    30: {
                      status: 3,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: true,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    60: {
                      status: 10,
                      details: [
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    90: {
                      status: 12,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    120: {
                      status: 16,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: true,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    150: {
                      status: 18,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    180: {
                      status: 20,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: true,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: false,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    210: {
                      status: 23,
                      details: [
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    240: {
                      status: 25,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    270: {
                      status: 26,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: true,
                        },
                        {
                          user: "Liam Carter",
                          status_success: false,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    300: {
                      status: 31,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    330: {
                      status: 34,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    365: {
                      status: 36,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                  },
                  {
                    month: "Feb",
                    30: {
                      status: 2,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    60: {
                      status: 7,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    90: {
                      status: 11,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    120: {
                      status: 16,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    150: {
                      status: 17,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    180: {
                      status: 22,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    210: {
                      status: 27,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    240: {
                      status: 34,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    270: {
                      status: 39,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    300: {
                      status: 41,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    330: {
                      status: 44,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                  },
                  {
                    month: "Mar",
                    30: {
                      status: 3,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    60: {
                      status: 15,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    90: {
                      status: 19,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    120: {
                      status: 24,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    150: {
                      status: 28,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    180: {
                      status: 37,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    210: {
                      status: 41,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    240: {
                      status: 46,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    270: {
                      status: 53,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    300: {
                      status: 54,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                  },
                  {
                    month: "Apr",
                    30: {
                      status: 5,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    60: {
                      status: 11,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    90: {
                      status: 14,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    120: {
                      status: 22,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    150: {
                      status: 27,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    180: {
                      status: 29,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    210: {
                      status: 36,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    240: {
                      status: 41,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    270: {
                      status: 50,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                  },
                  {
                    month: "May",
                    30: {
                      status: 4,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    60: {
                      status: 22,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    90: {
                      status: 25,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    120: {
                      status: 33,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    150: {
                      status: 43,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    180: {
                      status: 50,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    210: {
                      status: 56,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    240: {
                      status: 63,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                  },
                  {
                    month: "Jun",
                    30: {
                      status: 3,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    60: {
                      status: 39,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    90: {
                      status: 43,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    120: {
                      status: 50,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    150: {
                      status: 55,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    180: {
                      status: 58,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    210: {
                      status: 63,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                  },
                  {
                    month: "Jul",
                    30: {
                      status: 5,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    60: {
                      status: 40,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    90: {
                      status: 44,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    120: {
                      status: 52,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    150: {
                      status: 54,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    180: {
                      status: 58,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                  },
                  {
                    month: "Aug",
                    30: {
                      status: 7,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    60: {
                      status: 56,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    90: {
                      status: 60,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    120: {
                      status: 68,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    150: {
                      status: 76,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                  },
                  {
                    month: "Sep",
                    30: {
                      status: 12,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    60: {
                      status: 77,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    90: {
                      status: 79,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    120: {
                      status: 89,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                  },
                  {
                    month: "Oct",
                    30: {
                      status: 6,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    60: {
                      status: 81,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    90: {
                      status: 86,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                  },
                  {
                    month: "Nov",
                    30: {
                      status: 9,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                    60: {
                      status: 92,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                  },
                  {
                    month: "Dec",
                    30: {
                      status: 9,
                      details: [
                        {
                          user: "Jasmine Romo",
                          status_success: false,
                        },
                        {
                          user: "Liam Carter",
                          status_success: true,
                        },
                        {
                          user: "Penelope Nguyen",
                          status_success: true,
                        },
                        {
                          user: "Xavier Patel",
                          status_success: true,
                        },
                        {
                          user: "Grace Thompson",
                          status_success: false,
                        },
                        {
                          user: "Elijah Khan",
                          status_success: true,
                        },
                        {
                          user: "Isabella Chang",
                          status_success: true,
                        },
                        {
                          user: "Noah Sullivan",
                          status_success: false,
                        },
                        {
                          user: "Ava Ramirez",
                          status_success: true,
                        },
                        {
                          user: "Lucas Walker",
                          status_success: false,
                        },
                      ],
                    },
                  },
                ],
              },
            },
          ],
        };
        commit("setState", { key: "analyticsList", value: data.analytics });
      } catch (error) {
        console.error(error);
      } finally {
        commit("setState", { key: "analyticsListLoading", value: false });
      }
    },
    async getAssociatedUserList({ dispatch, commit }, organizationId) {
      const { data } = await axios.get(`/organization/associated_users/${organizationId}`);
      if (!data.success) {
        return dispatch("showMessage", { ...data, messageType: "error", messageTitle: "Error" }, { root: true });
      }
      let users = [...data.brokers, ...data.clients];
      commit("setState", { key: "associatedUsers", value: users });
      return data;
    },
    async changeBrokerBrokerage({ dispatch, commit }, payload) {
      let payloadData = { ...payload };
      let brokerId = payload.broker_id;
      delete payload.broker_id;
      const { data } = await axios.post(`/admin/broker/${brokerId}/brokerage`, payloadData);
      if (!data.success) {
        return dispatch("showMessage", { ...data, messageType: "error", messageTitle: "Error" }, { root: true });
      }
      dispatch("showMessage", { ...data, messageType: "success", messageTitle: "Success" }, { root: true });
      commit("updateBrokerageDetails", data);
      return data;
    },

    // PARTNERS ACTIONS
    async getPartnersList({ commit }) {
      commit("setState", { key: "partnersLoading", value: true });
      const { data } = await axios.get("/partners/");
      commit("setState", { key: "partnersLoading", value: false });

      if (data.success && data.results) {
        commit("setState", { key: "partnersList", value: data.results });
      }
    },

    async getPartnerDetails({ commit }, partnerId) {
      const { data } = await axios.get(`/partners/${partnerId}`);
      if (!data.success) {
        return;
      }
      return data.data;
    },

    async createPartner({ dispatch, commit }, payload) {
      commit("setState", { key: "isLoading", value: true });
      const { data } = await axios.post("/partners/", payload);
      commit("setState", { key: "isLoading", value: false });

      if (!data.success) {
        return dispatch("showMessage", { ...data, messageType: "error", messageTitle: "Error" }, { root: true });
      }

      dispatch(
        "showMessage",
        {
          message: data.message || "Partner created successfully",
          messageType: "success",
          messageTitle: "Success",
        },
        { root: true }
      );

      dispatch("getPartnersList");
      return data;
    },

    async updatePartner({ dispatch, commit }, payload) {
      commit("setState", { key: "isLoading", value: true });
      const partnerId = payload.id;
      delete payload.id;

      const { data } = await axios.put(`/partners/${partnerId}`, payload);
      commit("setState", { key: "isLoading", value: false });

      if (!data.success) {
        return dispatch("showMessage", { ...data, messageType: "error", messageTitle: "Error" }, { root: true });
      }

      dispatch(
        "showMessage",
        {
          message: data.message || "Partner updated successfully",
          messageType: "success",
          messageTitle: "Success",
        },
        { root: true }
      );

      dispatch("getPartnersList");
      return data;
    },

    // PARTNER ADS ACTIONS
    async getPartnerAdsList({ commit }, filters = {}) {
      commit("setState", { key: "partnerAdsLoading", value: true });

      const { data } = await axios.get("/partner_ads", { params: filters });
      commit("setState", { key: "partnerAdsLoading", value: false });

      if (data.success && data.results) {
        commit("setState", { key: "partnerAdsList", value: data.results });
        commit("setState", { key: "partnerAdsTotalCount", value: data.total_count });
        return data.results;
      }

      return [];
    },

    async updatePartnerAdStatus({ dispatch }, { adId, status }) {
      const { data } = await axios.post(`/partner_ads/${adId}/status`, { status });

      if (!data.success) {
        dispatch("showMessage", { ...data, messageType: "error", messageTitle: "Error" }, { root: true });
        return false;
      }

      dispatch(
        "showMessage",
        {
          message: `Ad ${status.toLowerCase()} successfully`,
          messageType: "success",
          messageTitle: "Success",
        },
        { root: true }
      );

      return true;
    },

    async bulkUpdatePartnerAdStatus({ dispatch }, { adIds, status }) {
      const { data } = await axios.post("/partner_ads/bulk_status", {
        partner_ad_ids: adIds,
        status,
      });

      if (!data.success) {
        dispatch("showMessage", { ...data, messageType: "error", messageTitle: "Error" }, { root: true });
        return false;
      }

      dispatch(
        "showMessage",
        {
          message: data.message || `${adIds.length} ad(s) updated successfully`,
          messageType: "success",
          messageTitle: "Success",
        },
        { root: true }
      );

      return true;
    },

    async savePartnerAd({ dispatch, state }, { adId, formData, status }) {
      const partnerId = state.partnersList.find((p) => p.name === formData.partnerName)?.id;

      if (!partnerId) {
        dispatch(
          "showMessage",
          {
            message: "Please select a partner",
            messageType: "error",
            messageTitle: "Error",
          },
          { root: true }
        );
        return null;
      }

      const payload = {
        partner_id: partnerId,
        title: formData.title,
        pitch: formData.description,
        cta_label: formData.ctaText,
        cta_action_type: formData.ctaActionType,
        partner_url: formData.partnerUrl,
        workflow_id: formData.workflowId || "",
        context_parameters: formData.contextParams || [],
        disclaimer_text: formData.disclaimerText,
        industry_filter_type: formData.industryStandard,
        industry_codes: formData.industryCodes,
        device_security_triggers: {
          condition: formData.triggerRules?.[0]?.logic || "AND",
          rules: formData.triggerRules || [],
        },
      };

      if (status) {
        payload.status = status;
      }

      const { data } = adId
        ? await axios.put(`/partner_ads/${adId}`, payload)
        : await axios.post("/partner_ads", payload);

      if (!data.success) {
        dispatch("showMessage", { ...data, messageType: "error", messageTitle: "Error" }, { root: true });
        return null;
      }

      dispatch(
        "showMessage",
        {
          message: adId ? "Ad updated successfully" : "Ad created successfully",
          messageType: "success",
          messageTitle: "Success",
        },
        { root: true }
      );

      return data.data;
    },

    async getPartnerAd({ dispatch, state }, adId) {
      const { data } = await axios.get(`/partner_ads/${adId}`);

      if (!data.success) {
        dispatch("showMessage", { ...data, messageType: "error", messageTitle: "Error" }, { root: true });
        return null;
      }

      const ad = data.data;
      const partner = state.partnersList.find((p) => p.id === ad.partner_id);

      let industryNames = [];
      if (ad.industry_codes && ad.industry_codes.length > 0 && ad.industry_filter_type !== "ALL") {
        const categories = await dispatch("getIndustriesNaics");
        if (categories) {
          const allData = Object.values(categories).flat();
          const industryMap = buildIndustryMap(allData);
          industryNames = ad.industry_codes.map((code) => industryMap[code] || code);
        }
      }

      return {
        partnerName: partner?.name || "",
        title: ad.title,
        description: ad.pitch,
        ctaText: ad.cta_label,
        ctaActionType: ad.cta_action_type,
        partnerUrl: ad.partner_url,
        workflowId: ad.workflow_id || "",
        contextParams: ad.context_parameters || [],
        disclaimerText: ad.disclaimer_text,
        industryStandard: ad.industry_filter_type,
        industryCodes: ad.industry_codes.join(","),
        industryNames,
        triggerRules: ad.device_security_triggers?.rules || [],
      };
    },

    async getCvesList(_, searchTerm = "") {
      const { data } = await axios.get("/api/cves/search", {
        params: { q: searchTerm },
      });

      if (data.success && data.results) {
        return data.results;
      }

      return [];
    },

    async getIndustriesNaics() {
      const { data } = await axios.get("/api/industries/naics");

      if (data.success && data.categories) {
        return data.categories;
      }

      return null;
    },
  },
};
